<template>
  <div class="wallets-container">
    <!-- Header -->
    <div class="page-header">
      <h1>Portefeuilles Fournisseurs</h1>
      <div class="header-actions">
        <router-link
          to="/wallets/transactions"
          class="btn btn-outline-primary"
        >
          <i class="fas fa-list" /> Toutes les Transactions
        </router-link>
      </div>
    </div>

    <!-- Statistics Cards -->
    <div
      v-if="stats"
      class="stats-grid"
    >
      <div class="stat-card pending">
        <div class="stat-icon">
          <i class="fas fa-clock" />
        </div>
        <div class="stat-content">
          <div class="stat-label">
            En Attente
          </div>
          <div class="stat-value">
            {{ formatAmount(stats.total_pending) }} DT
          </div>
          <div class="stat-sub">
            {{ stats.transactions_pending_count }} transactions
          </div>
        </div>
      </div>

      <div class="stat-card confirmed">
        <div class="stat-icon">
          <i class="fas fa-check-circle" />
        </div>
        <div class="stat-content">
          <div class="stat-label">
            Confirmé
          </div>
          <div class="stat-value">
            {{ formatAmount(stats.total_confirmed) }} DT
          </div>
          <div class="stat-sub">
            {{ stats.transactions_confirmed_count }} transactions
          </div>
        </div>
      </div>

      <div class="stat-card available">
        <div class="stat-icon">
          <i class="fas fa-wallet" />
        </div>
        <div class="stat-content">
          <div class="stat-label">
            Disponible
          </div>
          <div class="stat-value">
            {{ formatAmount(stats.total_available) }} DT
          </div>
          <div class="stat-sub">
            À payer aux fournisseurs
          </div>
        </div>
      </div>

      <div class="stat-card paid">
        <div class="stat-icon">
          <i class="fas fa-money-bill-wave" />
        </div>
        <div class="stat-content">
          <div class="stat-label">
            Payé
          </div>
          <div class="stat-value">
            {{ formatAmount(stats.total_paid) }} DT
          </div>
          <div class="stat-sub">
            {{ stats.total_suppliers }} fournisseurs
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters-section">
      <div class="filter-group search-group">
        <label>Rechercher</label>
        <input
          v-model="filters.search"
          type="text"
          placeholder="Nom du fournisseur..."
          @input="debounceSearch"
        >
      </div>

      <div class="filter-group">
        <label>Avec En Attente</label>
        <select
          v-model="filters.has_pending"
          @change="loadWallets"
        >
          <option value="">
            Tous
          </option>
          <option value="true">
            Oui
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label>Avec Confirmé</label>
        <select
          v-model="filters.has_confirmed"
          @change="loadWallets"
        >
          <option value="">
            Tous
          </option>
          <option value="true">
            Oui
          </option>
        </select>
      </div>

      <div class="filter-group">
        <label>&nbsp;</label>
        <button
          class="btn btn-secondary"
          @click="resetFilters"
        >
          <i class="fas fa-redo" /> Réinitialiser
        </button>
      </div>
    </div>

    <!-- Wallets Table -->
    <div class="table-container">
      <table
        v-if="wallets.length > 0"
        class="wallets-table"
      >
        <thead>
          <tr>
            <th>Fournisseur</th>
            <th>Email</th>
            <th class="text-right">
              En Attente
            </th>
            <th class="text-right">
              Confirmé
            </th>
            <th class="text-right">
              Payé
            </th>
            <th class="text-right">
              Disponible
            </th>
            <th class="text-right">
              Total
            </th>
            <th>Transactions</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="wallet in wallets"
            :key="wallet.id"
          >
            <td>
              <strong>{{ wallet.supplier_name }}</strong>
            </td>
            <td>{{ wallet.supplier_email }}</td>
            <td class="text-right amount pending">
              {{ formatAmount(wallet.pending_balance) }} DT
            </td>
            <td class="text-right amount confirmed">
              {{ formatAmount(wallet.confirmed_balance) }} DT
            </td>
            <td class="text-right amount paid">
              {{ formatAmount(wallet.paid_balance) }} DT
            </td>
            <td class="text-right amount available">
              {{ formatAmount(wallet.available_balance) }} DT
            </td>
            <td class="text-right amount total">
              {{ formatAmount(wallet.total_balance) }} DT
            </td>
            <td>
              <span class="badge badge-info">
                {{ wallet.transactions_count }}
              </span>
            </td>
            <td class="actions-cell">
              <router-link
                :to="`/wallets/show/${wallet.id}`"
                class="btn-action btn-view"
                title="Voir"
              >
                <i class="fas fa-eye" />
              </router-link>
            </td>
          </tr>
        </tbody>
      </table>

      <div
        v-else-if="!loading"
        class="empty-state"
      >
        <i class="fas fa-wallet" />
        <p>Aucun portefeuille trouvé</p>
      </div>

      <div
        v-if="loading"
        class="loading-state"
      >
        <i class="fas fa-spinner fa-spin" />
        <p>Chargement...</p>
      </div>
    </div>

    <!-- Pagination -->
    <div
      v-if="pagination.total > pagination.pageSize"
      class="pagination-container"
    >
      <button
        :disabled="pagination.page === 1"
        class="btn btn-sm"
        @click="changePage(pagination.page - 1)"
      >
        <i class="fas fa-chevron-left" />
      </button>
      <span class="page-info">
        Page {{ pagination.page }} / {{ Math.ceil(pagination.total / pagination.pageSize) }}
      </span>
      <button
        :disabled="pagination.page >= Math.ceil(pagination.total / pagination.pageSize)"
        class="btn btn-sm"
        @click="changePage(pagination.page + 1)"
      >
        <i class="fas fa-chevron-right" />
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'WalletsIndex',
  data() {
    return {
      wallets: [],
      stats: null,
      loading: false,
      filters: {
        search: '',
        has_pending: '',
        has_confirmed: '',
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0,
      },
      searchTimeout: null,
    }
  },
  created() {
    this.loadWallets()
    this.loadStats()
  },
  methods: {
    async loadWallets() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          page_size: this.pagination.pageSize,
        }

        if (this.filters.search) params.search = this.filters.search
        if (this.filters.has_pending) params.has_pending = this.filters.has_pending
        if (this.filters.has_confirmed) params.has_confirmed = this.filters.has_confirmed

        const response = await axios.get('/api/wallets/', { params })
        this.wallets = response.data.results || response.data
        this.pagination.total = response.data.count || this.wallets.length
      } catch (error) {
        this.$toast.error('Erreur lors du chargement des portefeuilles')
      } finally {
        this.loading = false
      }
    },

    async loadStats() {
      try {
        const response = await axios.get('/api/wallets/stats/')
        this.stats = response.data
      } catch (error) {
        // ignore error
      }
    },

    debounceSearch() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1
        this.loadWallets()
      }, 300)
    },

    resetFilters() {
      this.filters = {
        search: '',
        has_pending: '',
        has_confirmed: '',
      }
      this.pagination.page = 1
      this.loadWallets()
    },

    changePage(page) {
      this.pagination.page = page
      this.loadWallets()
    },

    formatAmount(amount) {
      if (!amount) return '0.000'
      return parseFloat(amount).toFixed(3)
    },
  },
}
</script>

<style lang="scss" scoped>
.wallets-container {
  padding: 1.5rem;
  max-width: 1600px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0;
  }

  .header-actions {
    display: flex;
    gap: 0.75rem;
  }
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
  }

  &.pending {
    border-left: 4px solid #f59e0b;
    .stat-icon {
      background: #fef3c7;
      color: #f59e0b;
    }
  }

  &.confirmed {
    border-left: 4px solid #10b981;
    .stat-icon {
      background: #d1fae5;
      color: #10b981;
    }
  }

  &.available {
    border-left: 4px solid #6366f1;
    .stat-icon {
      background: #e0e7ff;
      color: #6366f1;
    }
  }

  &.paid {
    border-left: 4px solid #8b5cf6;
    .stat-icon {
      background: #ede9fe;
      color: #8b5cf6;
    }
  }

  .stat-content {
    flex: 1;

    .stat-label {
      font-size: 0.875rem;
      color: #64748b;
    }

    .stat-value {
      font-size: 1.5rem;
      font-weight: 700;
      color: #1e293b;
    }

    .stat-sub {
      font-size: 0.75rem;
      color: #94a3b8;
    }
  }
}

.filters-section {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;

    &.search-group {
      flex: 1;
      min-width: 200px;
    }

    label {
      font-size: 0.75rem;
      font-weight: 500;
      color: #64748b;
    }

    input, select {
      padding: 0.5rem 0.75rem;
      border: 1px solid #e2e8f0;
      border-radius: 6px;
      font-size: 0.875rem;

      &:focus {
        outline: none;
        border-color: #6366f1;
      }
    }
  }
}

.table-container {
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.wallets-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 1rem;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    background: #f8fafc;
    font-weight: 600;
    font-size: 0.75rem;
    text-transform: uppercase;
    color: #64748b;
  }

  .text-right {
    text-align: right;
  }

  .amount {
    font-family: 'Monaco', 'Courier New', monospace;
    font-weight: 500;

    &.pending { color: #f59e0b; }
    &.confirmed { color: #10b981; }
    &.paid { color: #8b5cf6; }
    &.available { color: #6366f1; font-weight: 700; }
    &.total { color: #1e293b; font-weight: 700; }
  }

  .badge {
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    font-size: 0.75rem;
    font-weight: 500;

    &.badge-info {
      background: #e0e7ff;
      color: #6366f1;
    }
  }

  .actions-cell {
    .btn-action {
      padding: 0.5rem;
      border-radius: 6px;
      color: #64748b;

      &:hover {
        background: #f1f5f9;
        color: #6366f1;
      }
    }
  }
}

.empty-state, .loading-state {
  text-align: center;
  padding: 3rem;
  color: #64748b;

  i {
    font-size: 3rem;
    margin-bottom: 1rem;
    opacity: 0.5;
  }
}

.pagination-container {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1.5rem;

  .page-info {
    font-size: 0.875rem;
    color: #64748b;
  }
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  &.btn-primary {
    background: #6366f1;
    color: white;
    &:hover { background: #4f46e5; }
  }

  &.btn-outline-primary {
    background: white;
    color: #6366f1;
    border: 1px solid #6366f1;
    &:hover { background: #f0f0ff; }
  }

  &.btn-secondary {
    background: #f1f5f9;
    color: #64748b;
    &:hover { background: #e2e8f0; }
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}
</style>
